import { Column } from "exceljs";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DateValueType } from "react-tailwindcss-datepicker";
import { ListFieldsType, myDocumentsListName, siteUrl } from "src/api/config";
import {
  notificationsListName,
  notificationStatusesListName,
  templatesOWDListName,
} from "src/api/config/notifications.config";
import NotifyReportTable, { isFiltersObjectEmpty } from "src/components/tables/NotifyReportTableWithFilters";
import { NotificationIcon, ReportIcon } from "src/components/ui-icons/sidebar";
import { SearchUI } from "src/components/ui/Search";
import { TGCheckList } from "src/components/ui/TGCheckList";
import TGDatePicker from "src/components/ui/TGDatePicker";
import { TGDisclosure } from "src/components/ui/TGDisclosure";
import { TGSelectPicker, TGSelectUserPickerByGroup } from "src/components/ui/TGSelect";
import { urlDefault } from "src/constants";
import { useFiltersContext } from "src/contexts/FilterContext";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { useGetSelect } from "src/hooks/redux-selectors";
import useAsyncEffect from "src/hooks/useAsyncEffect";
import { useGetDocuments } from "src/hooks/useGetDocuments";
import ExportToExcel from "src/modules/exelExport/exelExport";
import { getItems } from "src/modules/spItems/actions";
import { selectCurrentUser, selectUsers } from "src/modules/users/selector";
import { filterUserSearchResults } from "src/pages/DashboardPage/acadia/components/Filters/ListsSearch";
import { RootState } from "src/store/rootReducer";
import { CheckBoxListItem, UserListItem } from "src/types/main";
import { buildSearchQuery } from "src/utils/api.utils";
import { resolveResourceUrl } from "src/utils/url.utils";
import { chunkArray, getName } from "src/utils/array.utils";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import {
  getNameAndSurname,
  mapArrayWithObject,
  nameFromEmail,
  prefix,
  removePrefix,
  shortNameFromEmail,
} from "src/utils/users.utils";
import DocumentFilters from "../../DashboardPage/acadia/components/Filters/DocumentFilters";
import { LayoutWithAsideAndContent } from "../../LayoutWithAsideAndContent";
import { EmployeeAttributeType, SubmitEmployeeState, SubmitFiltersType } from "../types";
import { removeInvisibleChar } from "src/utils/string.utils";
import { addUsers } from "src/modules/users/actions";
import { FiltersStateType } from "src/pages/DashboardPage/acadia/type";
import { isEqual } from "lodash";
import { useStatusContext } from "src/pages/DashboardPage/acadia/notificationPage";
import { FetchOptions, getItemsRequest } from "src/modules/spItems/api";
import { modifiedItemsState } from "src/modules/spItems/selectors";
import { GetFiltersType } from "src/modules/modules.type";
import Loading from "src/components/Loading";
import { strapiDefaultUrl, strapiUrl } from "src/modules/users/constants";
import axios from "axios";
import { isStrapi, notificationStatuses as notificationStatusesData } from "src/api/config/systemSwitch";

export function ReportPageFilterBreadcrumbLayout() {
  return (
    <nav className="flex justify-between w-full" aria-label="Breadcrumb">
      <FlexCol className="grow">
        <ol className="inline-flex items-center">
          <li className="inline-flex items-center" aria-current="page">
            <div className="inline-flex items-center space-x-2 text-base font-medium cursor-pointer">
              <ReportIcon />
              <span>Отчеты</span>
            </div>
            <span className="px-3 text-body-color">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M16.2 8.55001L10.3781 2.64376C10.125 2.39064 9.73126 2.39064 9.47813 2.64376C9.22501 2.89689 9.22501 3.29064 9.47813 3.54376L14.2031 8.35314H2.25001C1.91251 8.35314 1.63126 8.63439 1.63126 8.97189C1.63126 9.30939 1.91251 9.61876 2.25001 9.61876H14.2594L9.47813 14.4844C9.22501 14.7375 9.22501 15.1313 9.47813 15.3844C9.59063 15.4969 9.75938 15.5531 9.92813 15.5531C10.0969 15.5531 10.2656 15.4969 10.3781 15.3563L16.2 9.45001C16.4531 9.19689 16.4531 8.80314 16.2 8.55001Z"
                  fill={"#637381"}
                />
              </svg>
            </span>
          </li>
          <li className="inline-flex items-center" aria-current="page">
            <div className="inline-flex items-center space-x-2 text-base font-medium cursor-pointer">
              <NotificationIcon className="w-[18px] h-[18px]" />
              <span>Реестр уведомлений</span>
            </div>
          </li>
        </ol>
      </FlexCol>
    </nav>
  );
}

async function getNotifications(
  notifications: ListFieldsType[typeof notificationsListName][],
  filters: Partial<GetFiltersType<typeof notificationsListName>>,
  nextPage: SP.ListItemCollectionPosition | null,
) {
  const requestProps: FetchOptions<typeof notificationsListName> = {
    listName: notificationsListName,
    rowLimit: 500,
    filters,
    populateFields: ["Document", "VersionDocument", "NotificationID", "NotificationType", "Test"],
  };

  if (nextPage) {
    requestProps.nextPage = nextPage as any;
  }

  const response = await getItemsRequest(requestProps);

  const chunkNotifications = modifiedItemsState(response.items, notificationsListName);

  if (!response.nextPage) {
    return [...notifications, ...chunkNotifications];
  }

  return await getNotifications([...notifications, ...chunkNotifications], filters, response.nextPage);
}

export function NotificationsReport() {
  const { status } = useStatusContext();
  const [notifications, setNotifications] = useState<ListFieldsType[typeof notificationsListName][]>([]);
  const [downloading, setDownloading] = useState(false); // ❗️ Начинаем с false, чтобы управлять запуском

  const [submitFilters, setSubmitFilters] = useState<SubmitFiltersType>({
    notificationType: [],
    notificationStatus: [],
    employees: { audit: [], employee_owd: [], send: [], teacher_owd: [] },
    templateIDs: [],
    sendPeriodDate: {
      startDate: moment.utc().startOf("day").toDate(),
      endDate: moment.utc().endOf("day").toDate(),
    }, //! change 'day' to "month"
    completePeriodDate: { startDate: null, endDate: null },
  });

  function resetSubmitFilters() {
    setSubmitFilters({
      notificationType: [],
      notificationStatus: [],
      employees: { audit: [], employee_owd: [], send: [], teacher_owd: [] },
      templateIDs: [],
      sendPeriodDate: {
        startDate: moment().utc(true).startOf("day").toDate(),
        endDate: moment().utc(true).endOf("day").toDate(),
      },
      completePeriodDate: { startDate: null, endDate: null },
    });
  }

  async function changeSubmitFilters(filters: SubmitFiltersType) {
    setSubmitFilters(filters);
  }

  useAsyncEffect(async () => {
    setDownloading(true);
    try {
      const responseNotifications = await getNotifications([], getNotificationFilters(submitFilters), null);
      setNotifications(responseNotifications);
    } catch (error) {
      console.error("error :>> ", error);
    } finally {
      setDownloading(false);
    }
  }, [JSON.stringify(submitFilters), status]);

  return (
    <NotificationReportWrapper
      notifications={notifications}
      resetSubmitFilters={resetSubmitFilters}
      notificationLoading={downloading}
      changeSubmitFilters={changeSubmitFilters}
      submitFilters={submitFilters}
    />
  );
}

const filterKeyMap = {
  notificationType: "NotificationType",
  notificationStatus: "Completed",
  sendPeriodDate: "Created",
  completePeriodDate: "Completed",
  templateIDs: "TemplateOWDID",
};

function getNotificationFilters(filters: SubmitFiltersType) {
  const filteredNotifications = Object.keys(filters).reduce((acc: Record<string, any>, key) => {
    const filter = filters[key as keyof SubmitFiltersType];

    if (key === "notificationType") {
      if ((filter as CheckBoxListItem[]).length) {
        acc[filterKeyMap[key]] = (filter as CheckBoxListItem[]).map((f) => f.id);
      }
    } else if (key === "notificationStatus") {
      if ((filter as CheckBoxListItem[]).length > 0 && (filter as CheckBoxListItem[]).length < 2) {
        const isCompleted = (filter as CheckBoxListItem[]).some((f) => f.id === 1);

        if (!isCompleted) {
          acc[filterKeyMap[key]] = { isNull: true };
        }
      }
    } else if (key === "sendPeriodDate") {
      const value = filter as DateValueType;

      if (value?.startDate) {
        acc[filterKeyMap[key]] = {
          start: moment.utc(value.startDate).startOf("day").toDate(),
          end: moment.utc(value.endDate).endOf("day").toDate(),
        };
      }
    } else if (key === "completePeriodDate") {
      const value = filter as DateValueType;

      if (value?.startDate) {
        acc[filterKeyMap[key]] = {
          start: moment.utc(value.startDate).startOf("day").toDate(),
          end: moment.utc(value.endDate).endOf("day").toDate(),
        };
      }
    } else if (key === "templateIDs") {
      if ((filter as CheckBoxListItem[]).length) {
        acc[filterKeyMap[key]] = (filter as CheckBoxListItem[]).map((f) => f.id);
      }
    }

    return acc;
  }, {});

  return filteredNotifications;
}

function filterNotifications(
  notifications: ListFieldsType[typeof notificationsListName][],
  filters: SubmitFiltersType,
  documents: ListFieldsType[typeof myDocumentsListName][],
  documentFilters: FiltersStateType,
  searchQuery: string,
) {
  const noFiltersSelected =
    !isFiltersObjectEmpty(documentFilters) &&
    filters.notificationType.length === 0 &&
    filters.notificationStatus.length === 0 &&
    filters.employees.audit.length === 0 &&
    filters.employees.employee_owd.length === 0 &&
    filters.employees.send.length === 0 &&
    filters.employees.teacher_owd.length === 0 &&
    !filters.sendPeriodDate?.startDate &&
    !filters.sendPeriodDate?.endDate &&
    !filters.completePeriodDate?.startDate &&
    !filters.completePeriodDate?.endDate;

  const isCreatedDateEmpty =
    !isEqual(filters.sendPeriodDate?.startDate, moment.utc().toDate()) &&
    !isEqual(filters.sendPeriodDate?.endDate, moment.utc().toDate());

  if (noFiltersSelected && isCreatedDateEmpty) {
    // Если фильтры не выбраны, просто возвращаем все уведомления
    return notifications;
  }

  const filteredNotifications = notifications.filter((notification: ListFieldsType[typeof notificationsListName]) => {
    const documentMatch =
      isFiltersObjectEmpty(documentFilters) && documents.length > 0
        ? documents.some((d) => d.ID === notification.Document.ID)
        : true;

    const documentTitleMatch = searchQuery.trim()
      ? notification.Document.Title?.toLowerCase().includes(searchQuery.toLowerCase().trim())
      : true;

    const notificationTypeMatch = filters.notificationType.length
      ? filters.notificationType.some((d) => notification.NotificationType.ID === d.id)
      : true;

    const notificationStatusMatch =
      filters.notificationStatus.length && filters.notificationStatus.length === 1
        ? filters.notificationStatus[0].id === 1
          ? notification.Completed !== null &&
            notification.NotificationType.ID !== notificationStatusesData.approval && // 3 - согласовать
            notification.NotificationType.ID !== notificationStatusesData.revision // 9 - На доработку
          : notification.Completed === null
        : true;

    const notificationCompletePeriodDateMatch =
      filters.completePeriodDate?.startDate && filters.completePeriodDate.endDate
        ? notification.Completed !== null &&
          notification.NotificationType.ID !== notificationStatusesData.approval && // 3 - согласовать
          notification.NotificationType.ID !== notificationStatusesData.revision // 9 - На доработку
        : true;

    const notificationEmployeesSendMatch = filters.employees.send.length
      ? filters.employees.send.some((d) => notification.AuthorEmail.toLowerCase().includes(d.toLowerCase()))
      : true;

    const notificationEmployeesAuditMatch = filters.employees.audit.length
      ? filters.employees.audit.some(
          (d) =>
            notification.EmployeeEmail.toLowerCase().includes(d.toLowerCase()) &&
            notification.NotificationType.ID === notificationStatusesData.familiarization, // 5 - Ознакомление
        )
      : true;

    const notificationEmployeesTeacherOwdMatch = filters.employees.teacher_owd.length
      ? filters.employees.teacher_owd.some(
          (d) =>
            notification.EmployeeEmail.toLowerCase().includes(d.toLowerCase()) &&
            notification.NotificationType.ID === notificationStatusesData.OWD, // 6 - OWD
        )
      : true;

    const notificationEmployeesEmployeeOwdMatch = filters.employees.employee_owd.length
      ? filters.employees.employee_owd.some(
          (d) =>
            notification.TestedEmployee &&
            notification.TestedEmployee.toLowerCase().includes(d.toLowerCase()) &&
            notification.NotificationType.ID === notificationStatusesData.OWD, // 6 - OWD
        )
      : true;

    return (
      documentMatch &&
      documentTitleMatch &&
      notificationTypeMatch &&
      notificationStatusMatch &&
      notificationEmployeesSendMatch &&
      notificationEmployeesAuditMatch &&
      notificationCompletePeriodDateMatch &&
      notificationEmployeesTeacherOwdMatch &&
      notificationEmployeesEmployeeOwdMatch
    );
  });

  return filteredNotifications;
}

function NotificationReportWrapper({
  notifications,
  notificationLoading,
  resetSubmitFilters,
  submitFilters,
  changeSubmitFilters,
}: {
  notifications: ListFieldsType[typeof notificationsListName][];
  notificationLoading: boolean;
  resetSubmitFilters: () => void;
  submitFilters: SubmitFiltersType;
  changeSubmitFilters: (filters: SubmitFiltersType) => void;
}) {
  const dispatch = useAppDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [notificationsData, setNotificationsData] = useState<ListFieldsType[typeof notificationsListName][]>([]);

  const [loading, setLoading] = useState(false);

  const { filters } = useFiltersContext();

  const { documents, downloading: documentLoading } = useGetDocuments(filters, 500, isFiltersObjectEmpty(filters));

  function handleSearch(value: string) {
    setSearchQuery(value);
  }

  useEffect(() => {
    const documentFilterActive = isFiltersObjectEmpty(filters);

    if (documentFilterActive && documentLoading) {
      return;
    }

    if (!notificationLoading && !!notifications.length) {
      setLoading(true);

      const userEmails = new Set<string>();

      notifications.forEach((notification) => {
        const authorEmail = notification.AuthorEmail;
        const employeeEmail = notification.EmployeeEmail;

        if (authorEmail) userEmails.add(authorEmail.toLowerCase().trim());
        if (employeeEmail) userEmails.add(employeeEmail.toLowerCase().trim());
      });

      const chunks = chunkArray([...userEmails], 50);

      for (const chunk of chunks) {
        dispatch(addUsers(chunk));
      }

      const tempData = filterNotifications(notifications, submitFilters, documents, filters, searchQuery);

      setNotificationsData(tempData);
      setLoading(false);
    }
  }, [
    notifications,
    notificationLoading,
    documentLoading,
    JSON.stringify(filters),
    JSON.stringify(submitFilters),
    documents,
    searchQuery,
  ]);

  return (
    <LayoutWithAsideAndContent
      aside={
        <NotificationReportFilters
          submitFilters={submitFilters}
          resetSubmitFilters={resetSubmitFilters}
          changeSubmitFilters={changeSubmitFilters}
        />
      }
      content={
        <FlexCol className="w-full">
          <FlexRow className="px-7.5 h-[76px] border-b justify-between items-center shrink-0">
            <FlexCol>
              <ReportPageFilterBreadcrumbLayout />
            </FlexCol>
            {notificationsData.length > 0 && (
              <FlexCol>
                <ExportToExcelButton
                  loading={(isFiltersObjectEmpty(filters) && documentLoading) || notificationLoading || loading}
                  data={notificationsData}
                />
              </FlexCol>
            )}
          </FlexRow>
          <FlexRow className="px-7.5 mb-3.5">
            <FlexCol className="grow">
              <SearchUI placeholder="Поиск по названию документа" defaultValue={searchQuery} onClick={handleSearch} />
            </FlexCol>
          </FlexRow>
          <FlexRow className="px-7.5 pt-7.5">
            <NotifyReportTable
              loading={(isFiltersObjectEmpty(filters) && documentLoading) || notificationLoading || loading}
              items={notificationsData}
            />
          </FlexRow>
        </FlexCol>
      }
    />
  );
}

type ExportDataType = {
  columns: Partial<Column>[];
  data: any[];
};
const exportTempData: ExportDataType = {
  columns: [
    { header: "Код документа", key: "DocumentCode", width: 50 },
    { header: "Наименование", key: "DocumentName", width: 50 },
    { header: "Версия документа", key: "VersionDocument", width: 50 },
    { header: "Тип уведомления", key: "NotificationType", width: 30 },
    { header: "Дата отправления уведомления", key: "Created", width: 50 },
    { header: "Кому отправлено", key: "EmployeeEmail", width: 30 },
    { header: "Кем отправлено", key: "AuthorEmail", width: 40 },
    { header: "Дата завершения", key: "Completed", width: 40 },
    { header: "Ссылка на документ", key: "External_DocLink", width: 50 },
    { header: "Ссылка на уведомление", key: "External_NotLink", width: 50 },
  ],
  data: [],
};

function ExportToExcelButton({
  data,
  loading,
}: {
  data: ListFieldsType[typeof notificationsListName][];
  loading: boolean;
}) {
  const currentUser = useAppSelector((state: RootState) => selectCurrentUser(state));

  const users = useAppSelector((state) => selectUsers(state));
  const usersStatus = useAppSelector((state) => state.users.status);

  const [query, setQuery] = useSearchParams();

  function getNotificationCardLink(item: ListFieldsType[typeof notificationsListName]) {
    const newSearchParams = new URLSearchParams(query);

    const isAuthor = item.AuthorEmail === currentUser.email;

    if (
      item.NotificationType.ID === notificationStatusesData.approval ||
      item.NotificationType.ID === notificationStatusesData.cancel ||
      item.NotificationType.ID === notificationStatusesData.approved ||
      item.NotificationType.ID === notificationStatusesData.revision
    ) {
      if (item.NotificationID) {
        newSearchParams.set("notificationID", isAuthor ? item.NotificationID.ID.toString() : item.ID.toString());
      }

      if (!!item.VersionDocument) {
        let versionID = item.VersionDocument.ID;

        newSearchParams.set("version", versionID.toString());
      }

      newSearchParams.set("documentCard", item.Document.ID.toString());
    } else {
      newSearchParams.set("notificationCard", item.ID.toString());
    }

    return newSearchParams.toString();
  }

  const exportData = useMemo(() => {
    if (!loading && !!data.length && usersStatus === "IDLE") {
      const tempData: any[] = [];
      for (const d of data) {
        let authorUser = users[d.AuthorEmail] ? users[d.AuthorEmail]?.displayName : nameFromEmail(d.AuthorEmail);
        let employeeUser = users[d.EmployeeEmail]
          ? users[d.EmployeeEmail]?.displayName
          : nameFromEmail(d.EmployeeEmail);

        const baseData = {
          DocumentCode: d.DocumentCode,
          DocumentName: d.VersionDocument ? d.VersionDocument.Title : d.Document.Title,
          VersionDocument: (d.VersionDocument && (d.VersionDocument.Metadata.Ver || d.Document.Metadata.Ver || 1)) || 1,
          NotificationType: d.NotificationType.Title,
          Created: moment(d.Created).format("DD.MM.YYYY HH:mm"),
          EmployeeEmail: employeeUser || "",
          AuthorEmail: authorUser || "",
          Completed: d.Completed ? moment(d.Created).format("DD.MM.YYYY HH:mm") : "----",
          External_DocLink: {
            text: d.Document.Title,
            hyperlink: `${siteUrl}/dashboard/1/documents?documentCard=${d.Document.ID}`,
            tooltip: d.Document.Title,
          },
          External_NotLink: {
            text: d.Document.Title,
            hyperlink: `${siteUrl}/dashboard/1/reports/4?${getNotificationCardLink(d)}`,
            tooltip: d.Document.Title,
          },
        };

        tempData.push(baseData);
      }

      return {
        columns: exportTempData.columns,
        data: tempData,
      };
    } else {
      return {
        columns: exportTempData.columns,
        data: [],
      };
    }
  }, [loading, data, usersStatus, users]);

  const fileName = "Отчет уведомления";

  if (!exportData?.data.length || loading) return <Loading />;

  return (
    <ExportToExcel
      disabled={loading || !exportData || Object.keys(exportData).length === 0 || !exportData.data.length}
      exportData={exportData}
      fileName={fileName}
      type="notification_report"
    />
  );
}

export function ResetDocuments() {
  const { resetFilters } = useFiltersContext();

  function clearAll() {
    resetFilters();
  }

  return (
    <>
      <FlexRow
        className="flex-nowrap gap-x-2 items-center p-5 border-b cursor-pointer group"
        onClick={() => clearAll()}
      >
        <FlexCol className="group-hover:underline">Сбросить все фильтры</FlexCol>
        <FlexCol>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_381_6476)">
              <path
                d="M7.9999 7.19998L1.8999 1.09998C1.6749 0.874975 1.3249 0.874975 1.0999 1.09998C0.874902 1.32498 0.874902 1.67497 1.0999 1.89997L7.1999 7.99998L1.0999 14.1C0.874902 14.325 0.874902 14.675 1.0999 14.9C1.3249 15.125 1.6749 15.125 1.8999 14.9L7.9999 8.79998L14.0999 14.9C14.3249 15.125 14.6749 15.125 14.8999 14.9C14.9999 14.8 15.0749 14.65 15.0749 14.5C15.0749 14.35 15.0249 14.2 14.8999 14.1L8.7999 7.99998L14.8999 1.89997C14.9999 1.79997 15.0749 1.64997 15.0749 1.49998C15.0749 1.34998 15.0249 1.19998 14.8999 1.09998C14.6749 0.874975 14.3249 0.874975 14.0999 1.09998L7.9999 7.19998Z"
                fill="#637381"
              />
            </g>
            <defs>
              <clipPath id="clip0_381_6476">
                <rect width="16" height="16" fill="white" transform="translate(0 16) rotate(-90)" />
              </clipPath>
            </defs>
          </svg>
        </FlexCol>
      </FlexRow>
    </>
  );
}

function NotificationReportFilters({
  submitFilters,
  changeSubmitFilters,
  resetSubmitFilters,
}: {
  submitFilters: SubmitFiltersType;
  changeSubmitFilters: (e: SubmitFiltersType) => void;
  resetSubmitFilters: () => void;
}) {
  const { completePeriodDate, employees, notificationStatus, notificationType, sendPeriodDate, templateIDs } =
    submitFilters;

  const dispatch = useAppDispatch();

  const { filters, resetFilters: resetDocumentsFilters } = useFiltersContext();
  const { data: templates } = useGetSelect(templatesOWDListName);
  const { data: notificationStatuses } = useGetSelect(notificationStatusesListName);

  const [selectedEmployees, setSelectedEmployees] = useState<Record<EmployeeAttributeType, UserListItem[]>>({
    send: [],
    audit: [],
    employee_owd: [],
    teacher_owd: [],
  });

  const [selectedAttrEmployees, setSelectedAttrEmployees] = useState<
    Record<EmployeeAttributeType, SubmitEmployeeState>
  >({
    send: {
      departments: [],
      groupPeople: [],
      locations: [],
      positions: [],
      searchQuery: "",
    },
    audit: {
      departments: [],
      groupPeople: [],
      locations: [],
      positions: [],
      searchQuery: "",
    },
    employee_owd: {
      departments: [],
      groupPeople: [],
      locations: [],
      positions: [],
      searchQuery: "",
    },
    teacher_owd: {
      departments: [],
      groupPeople: [],
      locations: [],
      positions: [],
      searchQuery: "",
    },
  });

  function checkNotificationType(value: CheckBoxListItem) {
    let newValue = [...notificationType];
    if (newValue.length > 0 && newValue.find((f) => f.id === value.id)) {
      const filtered = notificationType.filter((n) => n.id !== value.id);

      newValue = filtered;
    } else {
      newValue.push(value);
    }

    changeSubmitFilters({ ...submitFilters, notificationType: newValue });
  }

  function checkNotificationStatus(value: CheckBoxListItem) {
    let newValue = [...notificationStatus];
    if (newValue.length > 0 && newValue.find((f) => f.id === value.id)) {
      const filtered = notificationStatus.filter((n) => n.id !== value.id);

      newValue = filtered;
    } else {
      newValue.push(value);
    }

    changeSubmitFilters({ ...submitFilters, notificationStatus: newValue });
  }

  function checkTemplateIDs(value: CheckBoxListItem) {
    let newValue = [...templateIDs];
    if (newValue.length > 0 && newValue.find((f) => f.id === value.id)) {
      const filtered = templateIDs.filter((n) => n.id !== value.id);

      newValue = filtered;
    } else {
      newValue.push(value);
    }

    changeSubmitFilters({ ...submitFilters, templateIDs: newValue });
  }

  function changeSendPeriodDate(value: DateValueType) {
    if (value) changeSubmitFilters({ ...submitFilters, sendPeriodDate: value });
  }

  function changeCompletePeriodDate(value: DateValueType) {
    if (value) changeSubmitFilters({ ...submitFilters, completePeriodDate: value });
  }

  function fetchEmployeesByType(type: EmployeeAttributeType, users: UserListItem[]) {
    setSelectedEmployees((prev) => ({ ...prev, [type]: users }));

    changeSubmitFilters({
      ...submitFilters,
      employees: {
        ...submitFilters.employees,
        [type]: mapArrayWithObject(users, "email"),
      },
    });
  }

  // Чистит всё
  function clearAll() {
    const resetData: Record<keyof SubmitFiltersType, any> = {
      notificationType: [],
      notificationStatus: [],
      employees: {
        send: {
          departments: [],
          groupPeople: [],
          locations: [],
          positions: [],
          searchQuery: "",
        },
        audit: {
          departments: [],
          groupPeople: [],
          locations: [],
          positions: [],
          searchQuery: "",
        },
        employee_owd: {
          departments: [],
          groupPeople: [],
          locations: [],
          positions: [],
          searchQuery: "",
        },
        teacher_owd: {
          departments: [],
          groupPeople: [],
          locations: [],
          positions: [],
          searchQuery: "",
        },
      },
      templateIDs: [],
      sendPeriodDate: { start: null, end: null },
      completePeriodDate: { start: null, end: null },
    };

    setSelectedEmployees({
      send: [],
      audit: [],
      employee_owd: [],
      teacher_owd: [],
    });

    setSelectedAttrEmployees(resetData.employees);

    resetSubmitFilters();
    resetDocumentsFilters();
  }

  function changeSelectedAttrEmployees(type: EmployeeAttributeType, options: SubmitEmployeeState) {
    setSelectedAttrEmployees((prev) => ({ ...prev, [type]: options }));
  }

  const isFiltersEmpty = useMemo(() => {
    const isEmployeesEmpty =
      employees.audit.length > 0 ||
      employees.employee_owd.length > 0 ||
      employees.send.length > 0 ||
      employees.teacher_owd.length > 0;

    const isSendDateEmpty =
      sendPeriodDate?.startDate &&
      moment(sendPeriodDate?.startDate).isSame(moment.utc().toDate()) &&
      moment(sendPeriodDate?.endDate).isSame(moment.utc().toDate());

    const isCompleteDateEmpty = completePeriodDate?.startDate;

    const isTypeEmpty = notificationType.length > 0;
    const isStatusEmpty = notificationStatus.length > 0;
    const isTemplateIDsEmpty = templateIDs.length > 0;

    return (
      isFiltersObjectEmpty(filters) ||
      !!isSendDateEmpty ||
      !!isCompleteDateEmpty ||
      isEmployeesEmpty ||
      isTypeEmpty ||
      isStatusEmpty ||
      isTemplateIDsEmpty
    );
  }, [filters, completePeriodDate, sendPeriodDate, JSON.stringify(submitFilters)]);

  useEffect(() => {
    dispatch(getItems({ listName: templatesOWDListName }));
    dispatch(
      getItems({
        listName: notificationStatusesListName,
        filters: {
          ID: [
            notificationStatusesData.approval,
            notificationStatusesData.acquainted,
            notificationStatusesData.acquaintedTest,
            notificationStatusesData.familiarization,
            notificationStatusesData.OWD,
            notificationStatusesData.cancel,
            notificationStatusesData.approved,
            notificationStatusesData.revision,
            notificationStatusesData.cancelNotification,
          ],
        },
      }),
    );
  }, []);

  return (
    <FlexCol className="overflow-y-auto scrollbar scrollbar-w-2 scrollbar-thumb-rounded-md scrollbar-thumb-[#D1D5DB] h-full">
      {isFiltersEmpty && (
        <FlexRow className="flex-nowrap gap-x-2 items-center p-5 border-b cursor-pointer group" onClick={clearAll}>
          <FlexCol className="group-hover:underline">Сбросить все фильтры</FlexCol>
          <FlexCol>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_381_6476)">
                <path
                  d="M7.9999 7.19998L1.8999 1.09998C1.6749 0.874975 1.3249 0.874975 1.0999 1.09998C0.874902 1.32498 0.874902 1.67497 1.0999 1.89997L7.1999 7.99998L1.0999 14.1C0.874902 14.325 0.874902 14.675 1.0999 14.9C1.3249 15.125 1.6749 15.125 1.8999 14.9L7.9999 8.79998L14.0999 14.9C14.3249 15.125 14.6749 15.125 14.8999 14.9C14.9999 14.8 15.0749 14.65 15.0749 14.5C15.0749 14.35 15.0249 14.2 14.8999 14.1L8.7999 7.99998L14.8999 1.89997C14.9999 1.79997 15.0749 1.64997 15.0749 1.49998C15.0749 1.34998 15.0249 1.19998 14.8999 1.09998C14.6749 0.874975 14.3249 0.874975 14.0999 1.09998L7.9999 7.19998Z"
                  fill="#637381"
                />
              </g>
              <defs>
                <clipPath id="clip0_381_6476">
                  <rect width="16" height="16" fill="white" transform="translate(0 16) rotate(-90)" />
                </clipPath>
              </defs>
            </svg>
          </FlexCol>
        </FlexRow>
      )}
      <TGDisclosure title="Типы уведомлений">
        <TGCheckList
          list={notificationStatuses.map((status) => ({
            id: status.ID,
            title: status.Title,
          }))}
          items={notificationType}
          onChecked={checkNotificationType}
        />

        <h4 className={"font-normal"}>Статус уведомлений</h4>
        <TGCheckList
          list={[
            { id: 1, title: "Завершено" },
            { id: 2, title: "Не завершено" },
          ]}
          items={notificationStatus}
          onChecked={checkNotificationStatus}
        />
      </TGDisclosure>
      <TGDisclosure title="Атрибуты документа" panelClassName="px-0">
        <DocumentFilters />
      </TGDisclosure>
      <EmployeeAttribute
        selectedAttrEmployees={selectedAttrEmployees}
        changeSelectedAttrEmployees={changeSelectedAttrEmployees}
        selectedEmployees={selectedEmployees}
        fetchEmployeesByType={fetchEmployeesByType}
      />
      <TGDisclosure title="Шаблоны OWD">
        <TGCheckList
          list={templates.map((templateOWD) => ({
            title: templateOWD.Title,
            id: templateOWD.ID,
          }))}
          items={templateIDs}
          onChecked={checkTemplateIDs}
        />
      </TGDisclosure>
      <TGDisclosure title="Даты" defaultOpen={true}>
        <FlexCol className="relative z-[100] w-full gap-y-3.5">
          <h4 className={"font-normal"}>Дата отправления (период)</h4>
          <TGDatePicker value={sendPeriodDate} onChange={changeSendPeriodDate} popoverDirection="down" />
        </FlexCol>
        <FlexCol className="relative z-[50] w-full gap-y-3.5">
          <h4 className={"font-normal"}>Дата завершения (период)</h4>
          <TGDatePicker value={completePeriodDate} onChange={changeCompletePeriodDate} popoverDirection="down" />
        </FlexCol>
      </TGDisclosure>
    </FlexCol>
  );
}

export function ResetFilters({ onReset }: { onReset: () => void }) {
  function clearAll() {
    onReset();
  }

  return (
    <>
      <FlexRow className="flex-nowrap gap-x-2 items-center p-5 border-b cursor-pointer group" onClick={clearAll}>
        <FlexCol className="group-hover:underline">Сбросить все фильтры</FlexCol>
        <FlexCol>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_381_6476)">
              <path
                d="M7.9999 7.19998L1.8999 1.09998C1.6749 0.874975 1.3249 0.874975 1.0999 1.09998C0.874902 1.32498 0.874902 1.67497 1.0999 1.89997L7.1999 7.99998L1.0999 14.1C0.874902 14.325 0.874902 14.675 1.0999 14.9C1.3249 15.125 1.6749 15.125 1.8999 14.9L7.9999 8.79998L14.0999 14.9C14.3249 15.125 14.6749 15.125 14.8999 14.9C14.9999 14.8 15.0749 14.65 15.0749 14.5C15.0749 14.35 15.0249 14.2 14.8999 14.1L8.7999 7.99998L14.8999 1.89997C14.9999 1.79997 15.0749 1.64997 15.0749 1.49998C15.0749 1.34998 15.0249 1.19998 14.8999 1.09998C14.6749 0.874975 14.3249 0.874975 14.0999 1.09998L7.9999 7.19998Z"
                fill="#637381"
              />
            </g>
            <defs>
              <clipPath id="clip0_381_6476">
                <rect width="16" height="16" fill="white" transform="translate(0 16) rotate(-90)" />
              </clipPath>
            </defs>
          </svg>
        </FlexCol>
      </FlexRow>
    </>
  );
}

function EmployeeAttribute({
  selectedEmployees,
  selectedAttrEmployees,
  changeSelectedAttrEmployees,
  fetchEmployeesByType,
}: {
  selectedEmployees: Record<EmployeeAttributeType, UserListItem[]>;
  selectedAttrEmployees: Record<EmployeeAttributeType, SubmitEmployeeState>;
  changeSelectedAttrEmployees: (type: EmployeeAttributeType, options: SubmitEmployeeState) => void;
  fetchEmployeesByType: (type: EmployeeAttributeType, users: UserListItem[]) => void;
}) {
  function changeAttrByType(type: EmployeeAttributeType, options: SubmitEmployeeState) {
    changeSelectedAttrEmployees(type, options);
  }

  function changeEmployeesByType(type: EmployeeAttributeType, users: UserListItem[]) {
    fetchEmployeesByType(type, users);
  }

  return (
    <TGDisclosure title="Атрибуты сотрудника" panelClassName="px-0">
      {/* {clearButton && <ResetFilters cb={resetEmployees} />} */}
      <TGDisclosure
        title="Кто отправил уведомления"
        wrapperClassName="border-none"
        buttonClassName="py-0"
        panelClassName="py-2.5"
      >
        <ContentArea
          initial={selectedAttrEmployees.send}
          type="send"
          selected={selectedEmployees.send}
          changeResult={changeEmployeesByType}
          changeAttr={changeAttrByType}
        />
      </TGDisclosure>
      <TGDisclosure
        title="Кто проходил(ит) ознакомление"
        wrapperClassName="border-none"
        buttonClassName="py-0"
        panelClassName="py-2.5"
      >
        <ContentArea
          initial={selectedAttrEmployees.audit}
          type="audit"
          selected={selectedEmployees.audit}
          changeResult={changeEmployeesByType}
          changeAttr={changeAttrByType}
        />
      </TGDisclosure>
      <TGDisclosure
        title="Кто проводил(ит) OWD"
        wrapperClassName="border-none"
        buttonClassName="py-0"
        panelClassName="py-2.5"
      >
        <ContentArea
          initial={selectedAttrEmployees.teacher_owd}
          type="teacher_owd"
          selected={selectedEmployees.teacher_owd}
          changeResult={changeEmployeesByType}
          changeAttr={changeAttrByType}
        />
      </TGDisclosure>
      <TGDisclosure
        title="Кому проводили(ят) OWD"
        wrapperClassName="border-none"
        buttonClassName="py-0"
        panelClassName="py-2.5"
      >
        <ContentArea
          initial={selectedAttrEmployees.employee_owd}
          type="employee_owd"
          selected={selectedEmployees.employee_owd}
          changeResult={changeEmployeesByType}
          changeAttr={changeAttrByType}
        />
      </TGDisclosure>
    </TGDisclosure>
  );
}

function ContentArea({
  type,
  initial,
  changeResult,
  changeAttr,
  selected,
  closeDisclosure,
}: {
  type: EmployeeAttributeType;
  initial: SubmitEmployeeState;
  changeResult: (type: EmployeeAttributeType, users: UserListItem[]) => void;
  changeAttr: (type: EmployeeAttributeType, options: SubmitEmployeeState) => void;
  selected: UserListItem[];
  closeDisclosure?: (focusableElement?: HTMLElement | React.MutableRefObject<HTMLElement | null>) => void;
}) {
  const searchInputRef = useRef<HTMLInputElement>(null);

  function handleSearch(value: string) {
    changeAttr(type, { ...initial, searchQuery: value });
  }

  useEffect(() => {
    async function fetchData() {
      if (
        !initial.searchQuery &&
        initial.departments.length === 0 &&
        initial.positions.length === 0 &&
        initial.locations.length === 0 &&
        initial.groupPeople.length === 0
      ) {
        changeResult(type, []);
        return;
      }

      if (initial.groupPeople.length) {
        const flatGroupPeople = initial.groupPeople
          .flatMap((group) => group.value)
          .map((email: string) => removeInvisibleChar(removePrefix(email, prefix)));

        const chunksPeople = chunkArray(flatGroupPeople, 10);

        const chunksPromisePeople = chunksPeople.map(async (chunkPeople) => {
          let searchQuery = buildSearchQuery(
            initial.searchQuery,
            initial.departments.map(getName),
            initial.positions.map(getName),
            initial.locations.map(getName),
            chunkPeople,
            [],
            [],
          );

          return axios.get(`${strapiUrl}users?${searchQuery}`, {
            headers: {
              Accept: "application/json;odata=nometadata",
              "Content-Type": "application/json;odata=nometadata",
            },
            withCredentials: true,
          });
        });

        const allResponses = await Promise.all(chunksPromisePeople);

        const employeesData: UserListItem[] = [];

        if (allResponses.length) {
          for (const responses of allResponses) {
            if (isStrapi) {
              if ("data" in responses && responses.data.length > 0) {
                const filterEmployeesData: UserListItem[] = responses.data.map((user: any) => {
                  const email = user.email;

                  const name = user.Name ? getNameAndSurname(user.Name, email) : nameFromEmail(email);
                  const shortName = user.Name ? getNameAndSurname(user.Name, email, true) : shortNameFromEmail(email);

                  return {
                    avatar: (user.PictureUrl && resolveResourceUrl(user.PictureUrl.url, strapiDefaultUrl)) || null,
                    name: name,
                    email: email,
                    id: user.id,
                    title: `${
                      getNameAndSurname(user.Name, user.email) || nameFromEmail(email)
                    } (${user.PersonalNumber || "Нет номера"})`,
                    personalNumber: user.PersonalNumber,
                    shortName: shortName,
                  };
                });
                employeesData.push(...filterEmployeesData);
              }
            } else {
              if ("get_value" in responses && (responses as any).get_value().ResultTables.length > 0) {
                const filterEmployeesData: UserListItem[] = filterUserSearchResults(
                  (responses as any).get_value().ResultTables[0].ResultRows,
                );

                employeesData.push(...filterEmployeesData);
              }
            }
          }
        }
        changeResult(type, employeesData);
      } else {
        let searchQuery = buildSearchQuery(
          initial.searchQuery,
          initial.departments.map(getName),
          initial.positions.map(getName),
          initial.locations.map(getName),
          [],
          [],
          [],
        );
        if (isStrapi) {
          const allResponses = await axios.get(`${strapiUrl}users?${searchQuery}`, {
            headers: {
              Accept: "application/json;odata=nometadata",
              "Content-Type": "application/json;odata=nometadata",
            },
            withCredentials: true,
          });
          const employeesData: UserListItem[] = [];
          if (allResponses.data.length > 0) {
            const filterEmployeesData: UserListItem[] = allResponses.data.map((user: any) => {
              const email = user.email;

              const name = user.Name ? getNameAndSurname(user.Name, email) : nameFromEmail(email);
              const shortName = user.Name ? getNameAndSurname(user.Name, email, true) : shortNameFromEmail(email);

              return {
                avatar: (user.PictureUrl && resolveResourceUrl(user.PictureUrl.url, strapiDefaultUrl)) || null,
                name: name,
                email: email,
                id: user.id,
                title: `${
                  getNameAndSurname(user.Name, user.email) || nameFromEmail(email)
                } (${user.PersonalNumber || "Нет номера"})`,
                personalNumber: user.PersonalNumber,
                shortName: shortName,
              };
            });
            employeesData.push(...filterEmployeesData);
          }
          changeResult(type, employeesData);
        }
      }
    }

    fetchData();
  }, [JSON.stringify(initial)]);

  return (
    <FlexCol className="grow custom-selects">
      <FlexRow>
        <TGSelectPicker
          values={initial.locations}
          onChange={(values) => changeAttr(type, { ...initial, locations: values })}
          fetchKey={["SPS-Location", "Region", "BaseOfficeLocation"]}
          classNames={{
            contentClassName: "text-base font-normal",
            selectClassName: "!px-2.5",
          }}
          dataField={{
            placeholder: "Выберите вариант",
            type: "peoplePicker",
            require: false,
            title: "Локация",
            width: "w-full",
            key: "peoplePicker",
            single: true,
          }}
          errors={{}}
          min={0}
        />
      </FlexRow>
      <FlexRow>
        <TGSelectUserPickerByGroup
          values={initial.groupPeople}
          onChange={(values) => changeAttr(type, { ...initial, groupPeople: values })}
          fetchKey="Groups"
          classNames={{
            contentClassName: "text-base font-normal",
            selectClassName: "!px-2.5",
          }}
          dataField={{
            placeholder: "Выберите группу",
            type: "peoplePicker",
            require: false,
            title: "Группа сотрудников",
            width: "w-full",
            key: "peoplePicker",
            single: true,
          }}
          clear={true}
          min={0}
          errors={{}}
        />
      </FlexRow>

      <FlexRow>
        <TGSelectPicker
          values={initial.departments}
          onChange={(values) => changeAttr(type, { ...initial, departments: values })}
          fetchKey={["DepartmentRUS", "Department"]}
          classNames={{
            contentClassName: "text-base font-normal",
            selectClassName: "!px-2.5",
          }}
          dataField={{
            placeholder: "Выберите отдел",
            type: "peoplePicker",
            require: false,
            title: "Отдел",
            width: "w-full",
            key: "peoplePicker",
            single: true,
          }}
          errors={{}}
          min={0}
        />
      </FlexRow>
      <FlexRow>
        <TGSelectPicker
          values={initial.positions}
          onChange={(values) => changeAttr(type, { ...initial, positions: values })}
          fetchKey={["JobTitleRus", "TitleRUS", "JobTitle"]}
          classNames={{
            contentClassName: "text-base font-normal",
            selectClassName: "!px-2.5",
          }}
          dataField={{
            placeholder: "Выберите должность",
            type: "peoplePicker",
            require: false,
            title: "Должность",
            width: "w-full",
            key: "peoplePicker",
            single: true,
          }}
          errors={{}}
          min={0}
        />
      </FlexRow>

      <FlexRow className="mb-5 hide-cancel-button">
        <SearchUI
          ref={searchInputRef}
          placeholder="Введите имя сотрудника"
          defaultValue={initial.searchQuery}
          onClick={handleSearch}
          label={"ФИО"}
          labelClass="text-black"
          inputClass="px-2.5 font-normal"
        />
      </FlexRow>
    </FlexCol>
  );
}
